import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

/*
  AssistantViewContext
  Purpose: lets a page tell Ask Cadenza what the user is looking at, so a
  question like "why does this flatten?" is sent with the run, heap or date
  on screen. Pages that don't report anything still get a page name from the
  route (see pageNames.js).

  Usage (in a page component):
    useAssistantView({ page: "Results", simulationId: selectedId });

  Fields (all optional): page, simulationId, heapId, date ("YYYY-MM-DD").
  Maestro re-checks every id against the conversation's project, so a page
  can't widen what the assistant may read.
*/

const AssistantViewContext = createContext({ view: null, setView: () => {} });

export const AssistantViewProvider = ({ children }) => {
  const [view, setView] = useState(null);
  const value = useMemo(() => ({ view, setView }), [view]);
  return (
    <AssistantViewContext.Provider value={value}>
      {children}
    </AssistantViewContext.Provider>
  );
};

/** Report what this page shows; cleared when the page unmounts. */
export const useAssistantView = (view) => {
  const { setView } = useContext(AssistantViewContext);
  const key = JSON.stringify(view || null);
  useEffect(() => {
    setView(view || null);
    return () => setView(null);
    // `key` captures the view's content; the object itself is new every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, setView]);
};

/** What the current page reported (for the assistant drawer). */
export const useReportedAssistantView = () => useContext(AssistantViewContext).view;
